// Установка как приложение (barter-025, по образцу Айлы lib/install.js).
// Android/Chrome и десктопный Chrome/Edge сами предлагают установку событием
// beforeinstallprompt — перехватываем его и показываем свою кнопку в Settings:
// нативный диалог по тапу, без копания в меню браузера. iOS Safari события
// не даёт вовсе — единственный путь «Поделиться → На экран „Домой“», в Settings
// показываем пошаговую подсказку.

export type InstallPlatform = 'ios' | 'android' | 'other';

export interface InstallState {
  /** запущено как установленное приложение (с главного экрана) */
  installed: boolean;
  /** браузер готов показать нативный диалог установки */
  canPrompt: boolean;
  platform: InstallPlatform;
}

let state: InstallState = { installed: false, canPrompt: false, platform: 'other' };
const listeners = new Set<() => void>();

let deferred: BeforeInstallPromptEvent | null = null; // живёт один prompt()

function set(patch: Partial<InstallState>): void {
  state = { ...state, ...patch };
  for (const l of listeners) l();
}

/** платформа по UserAgent: все браузеры на iOS — обёртки над WebKit, путь установки один */
export function detectPlatform(ua = '', maxTouchPoints = 0): InstallPlatform {
  const u = String(ua);
  if (/iPhone|iPod|iPad/.test(u)) return 'ios';
  // iPadOS 13+ притворяется маком: «Macintosh; Intel Mac OS X» + мультитач
  if (/Macintosh/.test(u) && maxTouchPoints > 1) return 'ios';
  if (/Android/.test(u)) return 'android';
  return 'other';
}

/** запущено как установленное приложение (display-mode: standalone/minimal-ui) */
export function isStandalone(win: Window = typeof window !== 'undefined' ? window : {} as Window): boolean {
  try {
    const nav = win.navigator as (Navigator & { standalone?: boolean }) | undefined;
    return nav?.standalone === true
      || !!win.matchMedia?.('(display-mode: standalone)')?.matches
      || !!win.matchMedia?.('(display-mode: minimal-ui)')?.matches;
  } catch {
    return false;
  }
}

/** интерфейс события beforeinstallprompt (в стандартных DOM-типах его нет) */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** подписка для useSyncExternalStore; возвращает отписку */
export function subscribeInstall(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** снапшот стейта для useSyncExternalStore */
export function getInstallState(): InstallState {
  return state;
}

/**
 * Повесить слушатели установки. win передаётся для тестов (Node без window).
 * Вызывать один раз — событие может прийти до открытия Settings.
 */
export function initInstall(win: Window | null = typeof window !== 'undefined' ? window : null): void {
  if (!win?.addEventListener) return;
  state = {
    installed: isStandalone(win),
    canPrompt: false,
    platform: detectPlatform(win.navigator?.userAgent || '', win.navigator?.maxTouchPoints || 0),
  };
  for (const l of listeners) l();

  win.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // без этого Chrome показывает свою мини-инфопанель
    deferred = e as BeforeInstallPromptEvent;
    set({ canPrompt: true });
  });
  win.addEventListener('appinstalled', () => {
    deferred = null;
    set({ canPrompt: false, installed: true });
  });
}

/** Тап «Установить»: показать нативный диалог. Событие одноразовое — после
 *  prompt() (принял или отклонил) повторно не используется; если Chrome решит
 *  предложить снова — beforeinstallprompt придёт ещё раз, слушатель жив. */
export async function promptInstall(): Promise<{ ok: boolean; outcome: string }> {
  if (!deferred) return { ok: false, outcome: 'unavailable' };
  const ev = deferred;
  deferred = null;
  set({ canPrompt: false });
  try {
    await ev.prompt();
  } catch {
    return { ok: false, outcome: 'error' };
  }
  let outcome = 'unknown';
  try {
    outcome = (await ev.userChoice)?.outcome || 'unknown';
  } catch {
    /* спросить уже нельзя */
  }
  if (outcome === 'accepted') {
    // appinstalled прилетит следом, но и сами перестрахуемся
    set({ installed: true, canPrompt: false });
  }
  return { ok: outcome === 'accepted', outcome };
}

/** только для юнит-тестов: чистый стейт между сценариями */
export function _resetForTests(): void {
  deferred = null;
  state = { installed: false, canPrompt: false, platform: 'other' };
  listeners.clear();
}
